// react-table depends on spreading props; we could name them explicitly but this component might
// break if the shape of the return value from row.getRowProps() changes
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Cell, Row, ColumnInstance } from 'react-table';

import {
    formatNumberCompact,
    getGapAnalysisStatementSelectionLogic,
} from '@trqk/utils';

import { summarizeRowsAbove } from '../../constants';
import { PerformancesTableButton } from '../../../../../../../nav';
import { URL_PARAMS, MODAL_IDS } from '../../../../../../../../constants';
import { useModal } from '../../../../../../../../hooks';
import { getFeatureFlags } from '../../../../../../../../featureFlags';

interface GapOrPerformancesCellProps {
    cell: Cell<TrqkGapChannelAudio, GapWorkPerformancesCellValue>;
    column: ColumnInstance<TrqkGapChannelAudio>;
    row: Row<TrqkGapChannelAudio>;
}

/**
 * For rows that do not correspond to a statement work, this component returns null.
 * For rows that correspond to a statement work, this component displays a formatted number. When
 * the performances number is greater than 0, it is rendered as a link to a filtered state of the
 * Performances table.
 * @param props - {@link PerformancesCellProps}
 * @returns JSX
 */
export function PerformancesCell({
    cell,
    row,
}: GapOrPerformancesCellProps): JSX.Element | string | null {
    if (row.subRows.length < summarizeRowsAbove) {
        return null;
    }

    const { gapAnalysisLinksToPerformancesTable } = getFeatureFlags();
    const performances = cell?.value?.performances ?? 0;
    const channelNameDetection = cell?.value?.channelNameDetection ?? null;
    const matchedWorkTitle = cell?.value?.matchedWorkTitle ?? null;
    const statementId = cell?.value?.statementId ?? null;

    const formattedPerformances = formatNumberCompact(performances);

    if (performances < 1 || !gapAnalysisLinksToPerformancesTable) {
        return formattedPerformances;
    }

    const { remove: removeModal } = useModal();

    const statementSelectionLogic =
        getGapAnalysisStatementSelectionLogic(statementId);

    return (
        <PerformancesTableButton
            onClick={() => {
                removeModal(MODAL_IDS.MANAGE_GAP_ANALYSIS, {
                    canceled: true,
                    success: false,
                });
            }}
            urlSearchParams={[
                {
                    key: URL_PARAMS.CHANNEL,
                    value: channelNameDetection,
                },
                {
                    key: URL_PARAMS.WORK_TITLE,
                    value: matchedWorkTitle,
                },
            ]}
            statementSelectionLogic={statementSelectionLogic}
        >
            <span className="underline text-primary-500">
                {formattedPerformances}
            </span>
        </PerformancesTableButton>
    );
}
